import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { overlay } from 'overlay-kit';
import { useGetOwnedClassrooms } from '@/api/gen/classroom/classroom.gen';
import { useGetTrialPointAllowance } from '@/api/gen/point-controller/point-controller.gen';
import { useUser } from '@/lib/auth';
import { CLASSROOM_ROUTES } from '@/shared/consts/routes';
import { CookieManager } from '@/shared/cookies';
import { markPromotionModalShown, wasPromotionModalShown } from '@/shared/features/promotion-modal-state';
import { PointReleaseModal } from '../components/PointReleaseModal';

/**
 * 「오늘은 그만 볼래요」를 누른 날만 기억한다.
 */
const POINT_RELEASE_DISMISSED_TODAY_COOKIE = 'point_release_dismissed_today';
const TRIAL_JELLY_INITIAL_BALANCE = 100;

/**
 * 포인트 출시 안내 모달 게이트.
 *
 * - 로그인한 **선생님 계정**이 무료 젤리 100개를 아직 한 번도 쓰지 않았을 때만 노출한다.
 * - 첫 지급 뒤에는 서버 잔액이 100보다 작아져 자동으로 영구 종료된다.
 * - 「오늘은 그만 볼래요」를 누르면 다음 날 자정까지 숨긴다.
 * - 로드형 모달이라 세션 게이트(`wasPromotionModalShown`)에 묶는다 —
 *   클래스 출시·휴대폰 인증 등 다른 자동 모달과 한 세션에 겹쳐 뜨지 않게 한다.
 *   (설명 모달은 온클릭이라 세션 게이트를 쓰지 않는 것과 대비)
 */
export const usePointReleaseModal = (triggeredOn = 'point_release_auto_promotion') => {
  const router = useRouter();
  const {
    isAuthenticated,
    isInitialized,
    user
  } = useUser();
  const isTeacher = user?.type === 'teacher';
  const allowanceQuery = useGetTrialPointAllowance({
    query: {
      enabled: isAuthenticated && isTeacher,
      throwOnError: false,
      meta: {
        ignoreError: true
      }
    }
  });
  const ownedClassroomsQuery = useGetOwnedClassrooms(undefined, {
    query: {
      enabled: isAuthenticated && isTeacher,
      throwOnError: false,
      meta: {
        ignoreError: true
      }
    }
  });
  const hasOpenedRef = useRef(false);
  const isDismissedForToday = useCallback(() => {
    return new CookieManager(POINT_RELEASE_DISMISSED_TODAY_COOKIE).has();
  }, []);
  const dismissForToday = useCallback(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    new CookieManager(POINT_RELEASE_DISMISSED_TODAY_COOKIE, {
      expires: tomorrow
    }).set('true');
  }, []);
  const handleStart = useCallback(() => {
    // 클래스 조회가 실패한 경우에는 새 클래스를 만들도록 단정하지 않고 목록으로 보낸다.
    if (ownedClassroomsQuery.isError) {
      void router.push(CLASSROOM_ROUTES.list());
      return;
    }
    const classrooms = ownedClassroomsQuery.data?.data ?? [];
    if (classrooms.length === 0) {
      void router.push(`${CLASSROOM_ROUTES.list()}?openCreate=true`);
      return;
    }
    if (classrooms.length === 1) {
      void router.push(CLASSROOM_ROUTES.studentList(classrooms[0].classGroupId));
      return;
    }
    void router.push(CLASSROOM_ROUTES.list());
  }, [ownedClassroomsQuery.data?.data, ownedClassroomsQuery.isError, router]);
  const openModal = useCallback(() => {
    overlay.open(({
      close,
      isOpen
    }) => <PointReleaseModal isOpen={isOpen} triggeredOn={triggeredOn} onClose={close} onDismissForToday={() => {
      dismissForToday();
      close();
    }} onStart={() => {
      close();
      handleStart();
    }} />);
  }, [dismissForToday, handleStart, triggeredOn]);
  const shouldShowModal = useCallback(() => {
    if (!isInitialized) return false;
    if (!isAuthenticated || !isTeacher) return false;
    if (allowanceQuery.isLoading || ownedClassroomsQuery.isLoading) return false;
    if (allowanceQuery.isError || ownedClassroomsQuery.isError) return false;
    const allowance = allowanceQuery.data?.data;
    if (!allowance?.limited || allowance.remaining !== TRIAL_JELLY_INITIAL_BALANCE) return false;
    if (isDismissedForToday()) return false;
    if (wasPromotionModalShown()) return false;
    return true;
  }, [allowanceQuery.data?.data, allowanceQuery.isError, allowanceQuery.isLoading, isAuthenticated, isDismissedForToday, isInitialized, isTeacher, ownedClassroomsQuery.isError, ownedClassroomsQuery.isLoading]);
  useEffect(() => {
    if (hasOpenedRef.current) return;
    if (shouldShowModal()) {
      hasOpenedRef.current = true;
      markPromotionModalShown();
      openModal();
    }
  }, [shouldShowModal, openModal]);
  return {
    openModal,
    dismissForToday,
    isDismissedForToday
  };
};